import React from 'react';
import { BrowserRouter, Navigate, Outlet, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAuth } from './hooks/useAuth';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import CustomerDashboard from './pages/CustomerDashboard';
import EngineerDashboard from './pages/EngineerDashboard';
import TLDashboard from './pages/TLDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import { ROLES } from './constants/roles';
import './styles/theme.css';

const getDefaultPathByRole = (role) => {
  switch (role) {
    case ROLES.ADMIN:
    case ROLES.BACKOFFICE:
      return '/admin/dashboard';
    case ROLES.MANAGER:
      return '/manager/dashboard';
    case ROLES.TL:
      return '/tl/engineers';
    case ROLES.ENGINEER:
      return '/engineer/dashboard';
    case ROLES.CUSTOMER:
      return '/customer/dashboard';
    default:
      return '/admin/dashboard';
  }
};

const FullScreenLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--background)',
    color: 'var(--text-primary)',
    fontSize: '18px'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
      <div>Loading AMC System...</div>
    </div>
  </div>
);

const LoginRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (user) {
    return <Navigate to={getDefaultPathByRole(user.role)} replace />;
  }

  return <Login />;
};

const RequireAuth = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const RequireRole = ({ allowedRoles }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultPathByRole(user.role)} replace />;
  }

  return <Outlet />;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={getDefaultPathByRole(user.role)} replace />;
};

const AppRoutes = () => {
  document.title = 'BION AMC';

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginRoute />} />

      <Route element={<RequireAuth />}>
        <Route element={<RequireRole allowedRoles={[ROLES.ADMIN, ROLES.BACKOFFICE]} />}>
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
        </Route>

        <Route element={<RequireRole allowedRoles={[ROLES.TL]} />}>
          <Route path="/tl" element={<Navigate to="/tl/engineers" replace />} />
          <Route path="/tl/*" element={<TLDashboard />} />
        </Route>

        <Route element={<RequireRole allowedRoles={[ROLES.ENGINEER]} />}>
          <Route path="/engineer" element={<Navigate to="/engineer/dashboard" replace />} />
          <Route path="/engineer/*" element={<EngineerDashboard />} />
        </Route>

        <Route element={<RequireRole allowedRoles={[ROLES.CUSTOMER]} />}>
          <Route path="/customer" element={<Navigate to="/customer/dashboard" replace />} />
          <Route path="/customer/*" element={<CustomerDashboard />} />
        </Route>

        <Route element={<RequireRole allowedRoles={[ROLES.MANAGER]} />}>
          <Route path="/manager" element={<Navigate to="/manager/dashboard" replace />} />
          <Route path="/manager/*" element={<ManagerDashboard />} />
        </Route>
      </Route>

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;